import React from "react";
import { Formik, Form, Field } from "formik";
import { Button, Row, Col, Form as BootstrapForm } from "react-bootstrap";
import { FaFilter, FaTimes, FaUndo, FaTag, FaIndustry, FaDollarSign, FaBoxes, FaLayerGroup } from "react-icons/fa";

const initialValues = {
  category: "All",
  stockType: "All",
  condition: "All",
  manufacturer: "",
  modelName: "",
  minPrice: "",
  maxPrice: "",
  supplier: "",
};

const StockFilters = ({ isOpen, onClose, onFilterChange, activeFiltersCount = 0 }) => {
  if (!isOpen) return null;

  const handleFormSubmit = (values) => {
    const activeFilters = Object.entries(values).reduce((acc, [key, val]) => {
      if (val && val !== "All") acc[key] = val;
      return acc;
    }, {});
    onFilterChange(activeFilters);
  };

  const handleReset = (resetForm) => {
    resetForm();
    onFilterChange({});
  };

  return (
    <div
      className="position-relative w-100 mb-3"
      style={{
        animation: "fadeInDown 0.18s cubic-bezier(0.16, 1, 0.3, 1)",
        zIndex: 20,
      }}
    >
      <div
        className="bg-white border rounded-3 shadow-sm p-3"
        style={{
          borderColor: "#e2e8f0",
          boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
          {({ handleSubmit, resetForm, values, dirty }) => (
            <Form>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-2"
                    style={{ width: 24, height: 24, background: "#f1f5f9", color: "#334155" }}
                  >
                    <FaFilter size={11} />
                  </div>
                  <span className="fw-bold text-dark" style={{ fontSize: "0.85rem", letterSpacing: "-0.01em" }}>
                    Advanced Filter Parameters
                  </span>
                  {activeFiltersCount > 0 && (
                    <span className="badge rounded-pill bg-primary" style={{ fontSize: "0.68rem" }}>
                      {activeFiltersCount} active
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted hover-text-dark"
                  onClick={onClose}
                  style={{ textDecoration: "none", fontSize: "1.1rem", lineHeight: 1 }}
                >
                  <FaTimes size={13} />
                </button>
              </div>

              {/* Grid Inputs */}
              <Row className="g-2">
                {/* Category */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Category
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0 py-1 text-muted">
                      <FaTag size={10} />
                    </span>
                    <Field as={BootstrapForm.Select} name="category" className="form-select form-select-sm border-start-0 shadow-none py-1">
                      <option value="All">All Categories</option>
                      <option value="Device">Devices</option>
                      <option value="Network Device">Network Devices</option>
                      <option value="Accessory">Accessories</option>
                      <option value="Spare Part">Spare Parts</option>
                    </Field>
                  </div>
                </Col>

                {/* Stock Type */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Tracking Type
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0 py-1 text-muted">
                      <FaLayerGroup size={10} />
                    </span>
                    <Field as={BootstrapForm.Select} name="stockType" className="form-select form-select-sm border-start-0 shadow-none py-1">
                      <option value="All">All Tracking Types</option>
                      <option value="Serialized">Serialized (IMEI/SN)</option>
                      <option value="Non-Serialized">Batch / Quantity</option>
                    </Field>
                  </div>
                </Col>

                {/* Condition */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Condition
                  </label>
                  <Field as={BootstrapForm.Select} name="condition" className="form-select form-select-sm shadow-none py-1">
                    <option value="All">Any Condition</option>
                    <option value="Brand New">Brand New</option>
                    <option value="Used">Used / Pre-Owned</option>
                    <option value="Refurbished">Refurbished</option>
                  </Field>
                </Col>

                {/* Supplier */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Supplier / Vendor
                  </label>
                  <Field type="text" name="supplier" className="form-control form-control-sm shadow-none py-1" placeholder="e.g. ABC Trading" />
                </Col>

                {/* Brand */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Brand / Maker
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0 py-1 text-muted">
                      <FaIndustry size={10} />
                    </span>
                    <Field type="text" name="manufacturer" className="form-control form-control-sm border-start-0 shadow-none py-1" placeholder="e.g. Apple, Samsung" />
                  </div>
                </Col>

                {/* Model */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Model Search
                  </label>
                  <Field type="text" name="modelName" className="form-control form-control-sm shadow-none py-1" placeholder="e.g. iPhone 15 Pro" />
                </Col>

                {/* Valuation Range */}
                <Col md={3} sm={6}>
                  <label className="text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Cost Range
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0 py-1 text-muted">
                      <FaDollarSign size={10} />
                    </span>
                    <Field type="number" name="minPrice" className="form-control form-control-sm border-start-0 shadow-none py-1" placeholder="Min" />
                    <Field type="number" name="maxPrice" className="form-control form-control-sm shadow-none py-1" placeholder="Max" />
                  </div>
                </Col>

                {/* Actions */}
                <Col md={3} sm={6} className="d-flex align-items-end">
                  <div className="d-flex gap-2 w-100">
                    <Button
                      variant="light"
                      size="sm"
                      className="border text-secondary flex-grow-1 py-1 fw-semibold"
                      style={{ fontSize: "0.78rem" }}
                      onClick={() => handleReset(resetForm)}
                      type="button"
                    >
                      <FaUndo className="me-1" size={10} /> Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-grow-1 py-1 fw-bold"
                      style={{ backgroundColor: "#001c3a", borderColor: "#001c3a", fontSize: "0.78rem" }}
                      onClick={handleSubmit}
                      type="button"
                    >
                      <FaFilter className="me-1" size={10} /> Apply
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default StockFilters;